import React, { useEffect, useState } from "react";
import { Box, CardContent } from "@mui/material";
import Header from "./Header";
import Submit from "./Submit";
import NoteBox from "./Notes/Notes";
import Citation from "./Citation";
import Summarize from "./Summarize";
import useCollection from "../hooks/useCollection.hook";
import EditBookmark from "./EditBookmark";
import { useSelector } from "react-redux";
import DeletePopup from "./DeletePopup";
import { setNote, setSelectedBookmarkOption } from "context/features/dataSlice";
import { useDispatch } from "react-redux";
import ReadingLevel from "./ReadingLevel";
import { useTrackedArticles } from "features/SourceSearch";
import { toast } from "react-toastify";
import { useUserData } from "features/User";

export default function BookmarkDetails({ toggleSidebar }) {
  const { isReadingLevelHidden } = useUserData();
  const [selectedCollection, setSelectedCollection] = useState("");
  const [edit, setEdit] = useState(false);
  const [articleData, setArticleData] = useState({});
  const [create, setCreate] = useState(false);
  const [citation, setCitation] = useState("");
  const [markdownSummary, setmarkdownSummary] = useState("");
  const [markdownLevel, setmarkdownLevel] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [authors, setAuthors] = useState([
    { firstName: "", lastName: "", creatorType: "author" },
  ]);
  const [deletePopup, setDeletePopup] = useState(false);
  const [citationLoaded, setCitationLoaded] = useState(false);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [levelLoaded, setLevelLoaded] = useState(false);
  const [originalArticleData, setOriginalArticleData] = useState({});
  const [originalAuthors, setOriginalAuthors] = useState([
    { firstName: "", lastName: "", creatorType: "author" },
  ]);
  const [originalSummary, setOriginalSummary] = useState("");
  const [originalLevelSummary, setOriginalLevelSummary] = useState("");

  const [disableCitation, setDisableCitation] = useState(false);
  const [disableSummary, setDisableSummary] = useState(false);
  const [disableLevel, setDisableLevel] = useState(true);
  const [isAIGeneratedContent, setIsAIGeneratedContent] = useState(false);
  const [isAIGeneratedLevel, setIsAIGeneratedLevel] = useState(false);

  const {
    level,
    selectedBookmarkOption,
    collectionId,
    summaryInfoData,
    archivedCollections,
    bookmarkedCollectionIds,
    isReadOnlyView,
  } = useSelector((state) => state.data);

  // A submission is viewed exactly like an archived collection: read-only throughout.
  const isArchived =
    isReadOnlyView ||
    (bookmarkedCollectionIds.length > 0 &&
      bookmarkedCollectionIds.every((id) =>
        archivedCollections.some(
          (collection) => Number(collection.collectionId) === Number(id)
        )
      ));

  const dispatch = useDispatch();

  useEffect(() => {
    if (isReadingLevelHidden && selectedBookmarkOption === "readinglevel") {
      dispatch(setSelectedBookmarkOption("notes"));
    }
  }, [dispatch, isReadingLevelHidden, selectedBookmarkOption]);

  const {
    saveNewCollection,
    editBookmark,
    createOrUpdateBookmark,
    toggleBookmarkCollection,
    saveBookmarkToCollections,
    deleteBookmarkSource,
  } = useCollection(
    setSelectedCollection,
    selectedStyle,
    authors,
    setmarkdownSummary,
    setmarkdownLevel,
    level,
    setOriginalSummary,
    setOriginalLevelSummary,
    isAIGeneratedContent,
    setIsAIGeneratedContent,
    isAIGeneratedLevel,
    setIsAIGeneratedLevel
  );

  const { hasBeenClicked, updateTrackedLink } = useTrackedArticles(
    summaryInfoData?.url
  );

  const closeSidebar = () => {
    setCitationLoaded(false);
  };

  const getDisabled = () => {
    if (create) return true;
    
    if (edit) return false;
    
    if (selectedBookmarkOption === "summary") {
      return disableSummary;
    }
    if (selectedBookmarkOption === "citation" && disableCitation) {
      return true;
    }
    if (selectedBookmarkOption === "readinglevel") {
      return disableLevel;
    }
    if (!hasBeenClicked && selectedBookmarkOption !== "citation") {
      return true;
    }
    return false;
  };

  const closedSidebarHandler = () => {
    dispatch(setNote({ noteId_ids: null }));
    toggleSidebar();
  };

  const handleSave = () => {
    if (edit) {
      toast.success("Bookmark saved to collection successfully!");
    } else if (selectedBookmarkOption === "summary") {
      toast.success("Summary saved to collection successfully");
    } else if (selectedBookmarkOption === "citation") {
      toast.success("Citation saved to collection successfully");
    } else if (selectedBookmarkOption === "readinglevel") {
      toast.success("Reading level saved to collection successfully");
    }
    setOriginalSummary(markdownSummary);
    setOriginalLevelSummary(markdownLevel);
    setDisableSummary(true);
    setDisableLevel(true);
  };

  useEffect(() => {
    updateTrackedLink(summaryInfoData?.url);
  }, [summaryInfoData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        hideReadingLevel={isReadingLevelHidden}
        toggleSidebar={closedSidebarHandler}
        selectedBookmarkOption={selectedBookmarkOption}
        edit={edit}
        setEdit={setEdit}
        saveNewCollection={saveNewCollection}
        setSelectedCollection={setSelectedCollection}
        selectedCollection={selectedCollection}
        toggleBookmarkCollection={toggleBookmarkCollection}
        saveBookmarkToCollections={saveBookmarkToCollections}
        closeSidebar={closeSidebar}
        setDeletePopup={setDeletePopup}
        create={create}
        setCreate={setCreate}
      />
      <CardContent sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 0 }}>
        {edit && <EditBookmark />}
        {deletePopup && (
          <DeletePopup
            deletePopup={deletePopup}
            setDeletePopup={setDeletePopup}
            deleteBookmarkSource={deleteBookmarkSource}
            toggleSidebar={toggleSidebar}
          />
        )}
        <Box
          sx={{
            display: edit ? "none" : "block",
            ...(isArchived && {
              pointerEvents: "none",
              opacity: 0.6,
            }),
          }}
        >
          <Citation
            citation={citation}
            setCitation={setCitation}
            articleData={articleData}
            setArticleData={setArticleData}
            selectedStyle={selectedStyle}
            setSelectedStyle={setSelectedStyle}
            authors={authors}
            setAuthors={setAuthors}
            selectedBookmarkOption={selectedBookmarkOption}
            citationLoaded={citationLoaded}
            setCitationLoaded={setCitationLoaded}
            setOriginalArticleData={setOriginalArticleData}
            originalArticleData={originalArticleData}
            originalAuthors={originalAuthors}
            setOriginalAuthors={setOriginalAuthors}
            setDisable={setDisableCitation}
            disable={disableCitation}
          />
          <NoteBox
            collectionId={collectionId}
            createBookmark={createOrUpdateBookmark}
            selectedBookmarkOption={selectedBookmarkOption}
          />
          {/* <Summarize
            markdownSummary={markdownSummary}
            setmarkdownSummary={setmarkdownSummary}
            summaryLoaded={summaryLoaded}
            setSummaryLoaded={setSummaryLoaded}
            selectedBookmarkOption={selectedBookmarkOption}
            setDisable={setDisableSummary}
            originalSummary={originalSummary}
            setOriginalSummary={setOriginalSummary}
            setIsAIGeneratedContent={setIsAIGeneratedContent}
            isAIGeneratedContent={isAIGeneratedContent}
          /> */}
          {!isReadingLevelHidden && <ReadingLevel
            levelLoaded={levelLoaded}
            setLevelLoaded={setLevelLoaded}
            markdownSummary={markdownLevel}
            setmarkdownSummary={setmarkdownLevel}
            level={level}
            setDisable={setDisableLevel}
            selectedBookmarkOption={selectedBookmarkOption}
            originalSummary={originalLevelSummary}
            setOriginalSummary={setOriginalLevelSummary}
            setIsAIGeneratedContent={setIsAIGeneratedLevel}
            isAIGeneratedContent={isAIGeneratedLevel}
          />}
        </Box>
      </CardContent>
      {(selectedBookmarkOption !== "notes" || edit) && !isArchived &&
        !(isReadingLevelHidden && selectedBookmarkOption === "readinglevel") && (
        <Submit
          articleData={articleData}
          citation={citation}
          markdownSummary={markdownSummary}
          addOrUpdateBookmark={editBookmark}
          setEdit={setEdit}
          edit={edit}
          citationLoaded={citationLoaded}
          disable={getDisabled()}
          selectedBookmarkOption={selectedBookmarkOption}
          levelLoaded={levelLoaded}
          markdownLevel={markdownLevel}
          summaryLoaded={summaryLoaded}
          handleSave={handleSave}
        />
      )}
    </Box>
  );
}
